import { RootState } from '@app/store'
import { createSlice } from '@reduxjs/toolkit'
import {
  api as authApi,
  OwnPublicUserProfile,
  TokenResponse,
} from '@app/services/auto-generated/auth'
import { LOGOUT, SET_ACCESS_TOKEN, SET_TOKENS, SET_USER, waitForRehydration } from './auth-actions'

interface authState {
  user: OwnPublicUserProfile | null
  tokens: TokenResponse | null
  userToVerify: null | { email: string }
  firstTime: null | boolean
  mode: 'english' | 'business' | undefined
  activeRole: 'teacher' | 'admin' | null
}

const initialState: authState = {
  user: null,
  tokens: null,
  userToVerify: null,
  firstTime: true,
  mode: undefined,
  activeRole: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUserToVerify: (state, { payload }) => {
      state.userToVerify = payload
    },
    setUserFirstTime: (state, { payload }) => {
      state.firstTime = payload
    },
    setMode: (state, { payload }) => {
      state.mode = payload
    },
    setActiveRole: (state, { payload }) => {
      state.activeRole = payload
    },
    toggleRole: (state) => {
      state.activeRole = state.activeRole === 'admin' ? 'teacher' : 'admin'
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(waitForRehydration.fulfilled, (state) => {})
      .addCase(SET_USER, (state, { payload }) => {
        state.user = payload
      })
      .addCase(SET_TOKENS, (state, { payload }) => {
        state.tokens = payload
      })
      .addCase(SET_ACCESS_TOKEN, (state, { payload }) => {
        if (state.tokens) {
          state.tokens.access_token = payload
        } else {
          state.tokens = { access_token: payload }
        }
      })
      .addCase(LOGOUT, () => {
        return { ...initialState, firstTime: false }
      })
      .addMatcher(authApi.endpoints.authWhoami.matchFulfilled, (state, { payload }) => {
        state.user = payload
      })
      .addMatcher(authApi.endpoints.authLogin.matchFulfilled, (state, { payload }) => {
        state.tokens = payload
      })
      .addMatcher(authApi.endpoints.authLoginGoogle.matchFulfilled, (state, { payload }) => {
        state.tokens = payload
      })
      .addMatcher(authApi.endpoints.authSsoCallback.matchFulfilled, (state, { payload }) => {
        state.tokens = payload
      })
      .addMatcher(authApi.endpoints.authRegisterEmail.matchFulfilled, (state, { payload }) => {
        state.tokens = payload
      })
  },
})

export default authSlice.reducer

export const { setUserToVerify, setUserFirstTime, setMode, setActiveRole, toggleRole } =
  authSlice.actions

export const selectUser = (state: RootState) => state.auth.user
export const selectTokens = (state: RootState) => state.auth.tokens
export const selectUserToVerify = (state: RootState) => state.auth.userToVerify
export const selectUserFirstTime = (state: RootState) => state.auth.firstTime
export const selectMode = (state: RootState) => state.auth.mode
export const selectActiveRole = (state: RootState) => state.auth.activeRole
